import { folderPathName, folderTrail } from '@lib/library/folderPath';
import { getLibraryUrl } from '@lib/utils/url';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { FolderGlyph } from './FolderGlyph';
import css from './MediaFolderLocation.module.scss';

/**
 * The folder a media lives in, as a small pill in its colour — shown only when that is not the
 * open folder, which happens while a search reaches into the folders below it
 * (`LibraryFolderStore.filesInView`). The full path is in the tooltip.
 *
 * It links to that folder, with the media highlighted there, and clears the search on the way:
 * the search is what showed the media outside its folder, and kept, it would hide the rest of the
 * folder the user asked to open.
 */
export const MediaFolderLocation = observer(function MediaFolderLocation({
  media,
  className,
}: {
  media: Media;
  className?: string;
}) {
  const folder = useLibraryFolder();
  const path = folder.folderOf(media);
  if (path === folder.currentFolderPath) return null;

  const trail = folderTrail(path, folder.library.name ?? 'Library');
  return (
    <Link
      to={getLibraryUrl(folder.library.libraryId, folder.library.name, {
        folderPath: path,
        highlightMediaId: media._id,
      })}
      className={classNames(css.location, className)}
      title={`Open ${trail}`}
      // Inside a card or row that opens the media, and that drags as the media.
      draggable={false}
      onClick={(event) => {
        event.stopPropagation();
        folder.setOmniFilter(null);
      }}
    >
      <FolderGlyph color={folder.foldersByPath.get(path)?.color} size={14} />
      <span className={css.name}>{folderPathName(path)}</span>
    </Link>
  );
});
